import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import './ProfileView.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Helper to format date safely ──────────────────────────────
const formatDate = (val) => {
  if (!val) return null;
  try { return new Date(val).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' }); }
  catch { return val; }
};

export default function ProfileView() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.status !== 'approved') return navigate('/login');
    API.get(`/api/profiles/${id}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Profile not found'))
      .finally(() => setLoading(false));
  }, [id, user, navigate]);

  const handleShortlist = async () => {
    try {
      const { data: res } = await API.post('/api/shortlist/toggle', { profile_id: id });
      toast.success(res.message);
      setData(d => ({ ...d, isShortlisted: res.action === 'added' }));
    } catch { toast.error('Failed'); }
  };

  const handleContact = async () => {
    try {
      await API.post('/api/contacts/request', { receiver_id: id });
      toast.success('Contact request sent!');
      setData(d => ({ ...d, contactRequest: { status: 'pending', sender_id: user.id } }));
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  if (loading) return <div style={{paddingTop:'72px'}}><div className="loader"><div className="spinner" /></div></div>;
  if (!data) return null;

  const { profile: p, matchData: m, isShortlisted, contactRequest } = data;

  const typeClass = {
    'Sutham': 'badge-sutham', 'Seva': 'badge-seva',
    'Rahu Ketu': 'badge-rahuketu', 'Seva, Rahu Ketu': 'badge-sevarahu'
  }[p?.horoscope_type] || '';

  return (
    <div className="profile-view-page">
      <div className="container pv-container">
        <button className="btn btn-outline btn-sm" onClick={() => navigate(-1)} style={{marginBottom:'20px'}}>← Back</button>

        <div className="pv-grid">
          {/* LEFT: Photo + Match */}
          <div className="pv-left">
            <div className="pv-photo-wrap">
              {p.photo_path ? (
                <img src={`${API_URL}${p.photo_path}`} alt={p.full_name} className="pv-photo" />
              ) : (
                <div className="pv-photo-ph">{p.profile_type === 'bride' ? '👰' : '🤵'}</div>
              )}
            </div>

            <div className="pv-badges">
              <span className="badge badge-approved">✓ Verified</span>
              <span className={`badge ${typeClass}`}>{p.horoscope_type}</span>
            </div>

            {/* Match Score */}
            {m && (
              <div className={`pv-match-card ${m.matchColor}`}>
                <div className={`match-ring ${m.matchColor}`}>
                  <span>{m.totalScore}%</span>
                </div>
                <div>
                  <strong>{m.matchLabel}</strong>
                  <div className="pv-stars">{'★'.repeat(m.stars)}{'☆'.repeat(5-m.stars)}</div>
                </div>
              </div>
            )}

            {/* Match Breakdown */}
            {m && m.breakdown && (
              <div className="pv-match-breakdown">
                <h4>Match Breakdown</h4>
                {m.breakdown.map((b, i) => (
                  <div key={i} className="mb-item">
                    <div className="mb-factor">{b.factor}</div>
                    <div className="mb-bar-wrap">
                      <div className="mb-bar" style={{width:`${(b.score/b.max)*100}%`}} />
                    </div>
                    <div className="mb-score">{b.score}/{b.max}</div>
                  </div>
                ))}
                <p className="mb-note">{m.isTypeMatch ? `✅ Horoscope types are compatible` : `❌ Horoscope types not compatible`}</p>
              </div>
            )}

            {/* Actions */}
            <div className="pv-actions">
              <button className={`btn ${isShortlisted ? 'btn-gold' : 'btn-outline'}`} onClick={handleShortlist}>
                {isShortlisted ? '❤️ Shortlisted' : '🤍 Shortlist'}
              </button>
              {!contactRequest ? (
                <button className="btn btn-primary" onClick={handleContact}>📞 Request Contact</button>
              ) : contactRequest.status === 'pending' ? (
                <button className="btn btn-outline" disabled>⏳ Request Sent</button>
              ) : contactRequest.status === 'approved' ? (
                <div className="pv-phone-revealed">
                  <span>📞 Phone Revealed!</span>
                  <strong>{p.user_phone}</strong>
                </div>
              ) : null}
            </div>
          </div>

          {/* RIGHT: Details */}
          <div className="pv-right">
            <div className="pv-name-section">
              <h1>{p.full_name}</h1>
              {p.full_name_tamil && <p className="pv-name-tamil">{p.full_name_tamil}</p>}
              <div className="pv-meta">
                <span>🎂 {p.age} years</span>
                {p.height && <span>📏 {p.height}</span>}
                {p.native_place && <span>📍 {p.native_place}</span>}
                {p.blood_group && <span>🩸 {p.blood_group}</span>}
              </div>
            </div>

            <div className="pv-sections">
              <ProfileSection title="🌟 Horoscope Details" titleTa="ஜோதிட விவரங்கள்">
                <DetailRow label="God Name / தெய்வம்" value={p.god_name} />
                <DetailRow label="Birth Star / நட்சத்திரம்" value={p.birth_star} />
                <DetailRow label="Rasi / ராசி" value={p.rasi} />
                <DetailRow label="Lagnam / லக்னம்" value={p.lagnam} />
                <DetailRow label="Horoscope Type" value={p.horoscope_type} />
                <DetailRow label="Date of Birth / பிறந்த தேதி" value={formatDate(p.dob)} />
                <DetailRow label="Time of Birth / பிறந்த நேரம்" value={p.tob} />
              </ProfileSection>

              <ProfileSection title="🎓 Education & Career" titleTa="கல்வி மற்றும் தொழில்">
                <DetailRow label="Education / கல்வி" value={p.education} />
                <DetailRow label="Profession / தொழில்" value={p.job_profession} />
                <DetailRow label="Workplace / பணிமனை" value={p.workplace} />
                <DetailRow label="Income / வருமானம்" value={p.income} />
              </ProfileSection>

              <ProfileSection title="👨‍👩‍👧 Family Details" titleTa="குடும்ப விவரங்கள்">
                <DetailRow label="Father / தந்தை" value={p.father_name} />
                <DetailRow label="Mother / தாயார்" value={p.mother_name} />
                <DetailRow label="Siblings / உடன்பிறந்தோர்" value={p.siblings_count} />
                <DetailRow label="Siblings Married" value={p.siblings_married} />
                <DetailRow label="Caste / சாதி" value={p.caste_subdivision} />
                <DetailRow label="Property / சொத்து" value={p.has_property} />
              </ProfileSection>

              <ProfileSection title="📍 Location Details" titleTa="இட விவரங்கள்">
                <DetailRow label="Place of Birth / பிறந்த ஊர்" value={p.place_of_birth} />
                <DetailRow label="Native Place / சொந்த ஊர்" value={p.native_place} />
                <DetailRow label="Address / முகவரி" value={p.address} />
              </ProfileSection>

              {p.horoscope_image_path && (
                <ProfileSection title="📄 Birth Horoscope" titleTa="ஜாதகம்">
                  <a href={`${API_URL}${p.horoscope_image_path}`} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">
                    📄 View Horoscope
                  </a>
                </ProfileSection>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileSection({ title, titleTa, children }) {
  return (
    <div className="pv-section">
      <div className="pvs-header">
        <h3>{title}</h3>
        <span>{titleTa}</span>
      </div>
      <div className="pvs-body">{children}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="pv-detail-row">
      <span className="pdr-label">{label}</span>
      <span className="pdr-value">{value}</span>
    </div>
  );
}